import { useEffect, useMemo, useState } from "react";

import {
  defaultLoadingPhases,
  playLoadingPhases,
} from "@/blocks/loading/phases";

type LoadingBlockProps = {
  title: string;
  detail: string;
  eyebrow?: string;
  etaSeconds?: number;
  variant?: "panel" | "compact";
  mode?: "default" | "play";
};

function formatEta(seconds: number) {
  const safeSeconds = Math.max(0, seconds);
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}

export function LoadingBlock({
  title,
  detail,
  eyebrow = "Runtime status",
  etaSeconds = 7,
  variant = "panel",
  mode = "default",
}: LoadingBlockProps) {
  const phases = useMemo(
    () => (mode === "play" ? [...playLoadingPhases] : [...defaultLoadingPhases]),
    [mode],
  );
  const [eta, setEta] = useState(etaSeconds);
  const [phaseIndex, setPhaseIndex] = useState(0);

  useEffect(() => {
    setEta(etaSeconds);
  }, [etaSeconds]);

  useEffect(() => {
    const countdown = window.setInterval(() => {
      setEta((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => window.clearInterval(countdown);
  }, []);

  useEffect(() => {
    const phaseAdvance = window.setInterval(() => {
      setPhaseIndex((current) => (current + 1) % phases.length);
    }, 1400);

    return () => window.clearInterval(phaseAdvance);
  }, [phases.length]);

  const progressRatio = (phaseIndex + 1) / phases.length;

  return (
    <section
      className={`loading-block loading-block--${variant}`}
      aria-live="polite"
      aria-busy="true"
    >
      <div className="loading-block__head">
        <p className="loading-block__eyebrow">{eyebrow}</p>
        <div className="loading-block__meta">
          <span className="loading-block__state">Active</span>
          <span className="loading-block__eta">ETA {formatEta(eta)}</span>
        </div>
      </div>

      <div className="loading-block__copy">
        <h2>{title}</h2>
        <p>{detail}</p>
      </div>

      <div className="loading-block__rail" aria-hidden="true">
        <div
          className="loading-block__rail-fill"
          style={{ width: `${progressRatio * 100}%` }}
        />
      </div>

      <div className="loading-block__pulse" aria-hidden="true" />

      <ol className="loading-block__phases">
        {phases.map((phase, index) => (
          <li
            key={phase}
            className={
              index === phaseIndex
                ? "loading-block__phase loading-block__phase--active"
                : index < phaseIndex
                  ? "loading-block__phase loading-block__phase--complete"
                  : "loading-block__phase"
            }
          >
            <span className="loading-block__phase-index">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="loading-block__phase-label">{phase}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}
